import qs from "qs";
import { useEffect, useState } from "react";
import { useLink } from "solito/link";
import { LogOut as LogOutIcon } from "@tamagui/lucide-icons";
import { isWeb, openMailto, Settings, Switch, usePathname } from "@medbillai/ui";
import { Flag } from "@medbillai/utils";
import { peopleUrl, settingsAccountSettingsUrl, settingsReceiptsUrl, settingsReleaseListUrl } from "@medbillai/utils/urls";
import { ScreenView } from "../../components/ScreenView";
import { analyticsService, LogoutReason } from "../../lib/analytics";
import { useAuth } from "../../lib/auth";
import { useBoolVariation } from "../../lib/flag";
import { PushPermissionStatus, pushNotificationsDeniedAlert, pushNotificationsDisableAlert, pushNotificationsErrorAlert, pushNotificationsService } from "../../lib/push-notifications";
import { useAppState } from "../../lib/useAppState";
import { useBugReportingBody } from "../../lib/useBugReportingBody";
import { useLogout } from "../../lib/useLogout";
import { useAlerts } from "../../provider/alerts";
import { SettingsFooter } from "./components/SettingsFooter";

/*
 * Our settings are currently a mix bag of internal and external pages/actions
 * We aren't optimizing for accessibility at the moment and some links here
 * aren't marked as such.
 */
export const SettingsScreen = () => {
  const pathname = usePathname();
  const {
    user
  } = useAuth();
  const accountEmail = user?.profile?.email?.trim() ?? "";
  const bugReportingBody = useBugReportingBody();
  const {
    isActive
  } = useAppState();
  const {
    showAlertDialog
  } = useAlerts();
  const [pushPermissionStatus, setPushPermissionStatus] = useState<PushPermissionStatus | undefined>(undefined);
  const logout = useLogout();
  const showPayments = useBoolVariation(Flag.SHOW_PAYMENT_IN_SETTINGS, false);
  const paymentsUrl = useLink({
    href: settingsReceiptsUrl()
  });
  const peopleLink = useLink({
    href: peopleUrl()
  });
  const releaseListLink = useLink({
    href: settingsReleaseListUrl()
  });
  const accountLink = useLink({
    href: settingsAccountSettingsUrl()
  });
  const handleLogout = () => {
    showAlertDialog({
      title: "Are you sure?",
      message: "Please confirm that you wanted to log out.",
      onAccept: () => {
        logout({
          reason: LogoutReason.USER_LOGOUT,
          useReplace: true
        });
      },
      cancellable: true,
      acceptTestID: "settings.logout-confirm"
    });
  };

  // Used to keep permissionsStatus current when exiting and re-entering the app.
  useEffect(() => {
    if (!isActive) {
      return;
    }
    void (async () => {
      const resp = await pushNotificationsService.getPermissionStatus();
      if (resp.success) {
        return setPushPermissionStatus(resp.permissionStatus);
      } else {
        // On error set to default permissions
        setPushPermissionStatus(PushPermissionStatus.Denied);
      }
    })();
  }, [isActive]);
  const updatePushNotificationPermissions = async (checked: boolean) => {
    if (checked) {
      analyticsService.trackEvent("ClickedEnableNotifications", {});
      const status = await pushNotificationsService.showPermissionStatusPrompt();
      if (status.success) {
        // If success set new value and do not route user
        if (status.permissionStatus === PushPermissionStatus.Granted) {
          analyticsService.trackEvent("ClickedEnableNotificationsSuccess", {});
          setPushPermissionStatus(PushPermissionStatus.Granted);
        } else {
          analyticsService.trackEvent("ClickedEnableNotificationsDenied", {});
          showAlertDialog(pushNotificationsDeniedAlert());
        }
      } else {
        // If error show error prompt
        analyticsService.trackEvent("ClickedEnableNotificationsError", {});
        showAlertDialog(pushNotificationsErrorAlert());
      }
    } else {
      // If user is turning off push notifications
      // Show alert to confirm
      analyticsService.trackEvent("ClickedDisableNotifications", {});
      showAlertDialog(pushNotificationsDisableAlert());
    }
  };
  return <ScreenView disableScroll noPadding backgroundColor="$surface1" stackProps={{
    pt: "$6",
    backgroundColor: "$surface1"
  }} renderAfter={<SettingsFooter />} data-sentry-element="ScreenView" data-sentry-component="SettingsScreen" data-sentry-source-file="screen.tsx">
      <Settings data-sentry-element="Settings" data-sentry-source-file="screen.tsx">
        <Settings.Items data-sentry-element="Settings.Items" data-sentry-source-file="screen.tsx">
          <Settings.Group data-sentry-element="Settings.Group" data-sentry-source-file="screen.tsx">
            <Settings.Item isActive={pathname === peopleUrl()} {...peopleLink} data-sentry-element="Settings.Item" data-sentry-source-file="screen.tsx">
              Profiles
            </Settings.Item>
            <Settings.Item isActive={pathname === settingsReleaseListUrl()} {...releaseListLink} data-sentry-element="Settings.Item" data-sentry-source-file="screen.tsx">
              HIPAA Release Forms
            </Settings.Item>
            {!isWeb ? <Settings.Item customElement={<Switch size="$2" checked={pushPermissionStatus === PushPermissionStatus.Granted} disabled={pushPermissionStatus === undefined} onCheckedChange={updatePushNotificationPermissions}>
                    <Switch.Thumb />
                  </Switch>}>
                Notifications
              </Settings.Item> : null}
          </Settings.Group>

          <Settings.Group data-sentry-element="Settings.Group" data-sentry-source-file="screen.tsx">
            <Settings.Item onPress={() => openMailto("mailto:support@grantedhealth.com")} data-sentry-element="Settings.Item" data-sentry-source-file="screen.tsx">
              Contact us
            </Settings.Item>
            <Settings.Item onPress={() => openMailto(`mailto:support@grantedhealth.com?${qs.stringify({
            subject: "Bug Report",
            body: bugReportingBody
          })}`)} data-sentry-element="Settings.Item" data-sentry-source-file="screen.tsx">
              Report a bug
            </Settings.Item>
            {showPayments ? <Settings.Item isActive={pathname === settingsReceiptsUrl()} {...paymentsUrl}>
                Payments
              </Settings.Item> : null}
            <Settings.Item isActive={pathname === settingsAccountSettingsUrl()} subtitle={accountEmail.length > 0 ? accountEmail : "—"} {...accountLink} data-sentry-element="Settings.Item" data-sentry-source-file="screen.tsx">
              Account
            </Settings.Item>
          </Settings.Group>
          <Settings.Group data-sentry-element="Settings.Group" data-sentry-source-file="screen.tsx">
            <Settings.Item
          // biome-ignore lint/complexity/noUselessFragments: Null doesn't work here
          customElement={<></>} secondaryItem externalUrl={{
            title: "Privacy Policy",
            url: "https://grantedhealth.com/granted/legal/privacy"
          }} data-sentry-element="Settings.Item" data-sentry-source-file="screen.tsx">
              Privacy Policy
            </Settings.Item>
            <Settings.Item
          // biome-ignore lint/complexity/noUselessFragments: Null doesn't work here
          customElement={<></>} secondaryItem externalUrl={{
            title: "Terms of Service",
            url: "https://grantedhealth.com/granted/legal/terms"
          }} data-sentry-element="Settings.Item" data-sentry-source-file="screen.tsx">
              Terms of Service
            </Settings.Item>
            <Settings.Item icon={LogOutIcon} secondaryItem onPress={handleLogout} testID="settings.logout" data-sentry-element="Settings.Item" data-sentry-source-file="screen.tsx">
              Log out
            </Settings.Item>
          </Settings.Group>
        </Settings.Items>
      </Settings>
    </ScreenView>;
};